import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Boxes,
  CircleCheck,
  ExternalLink,
  History as HistoryIcon,
  Link2,
  Pencil,
  Plus,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatMoney, CURRENCIES } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useInventoryItemDetail,
  useInventoryMovements,
  useInventoryCategories,
  usePatchInventoryItem,
  useStockMovement,
  hasStock,
  KIND_LABELS,
  type InventoryCustomFieldValue,
  type InventoryItem,
  type InventoryItemDetail,
  type InventoryKind,
} from "@/lib/inventory";
import { CustomFieldInput } from "./CustomFieldInput";
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter } from "./Drawer";
import { Tabs } from "./Tabs";
import { StatusBadge } from "./StatusBadge";
import { ConfirmDialog } from "./ConfirmDialog";
import { EmptyState } from "./EmptyState";
import { FieldPair } from "./DataTable";
import { useToast } from "./Toast";

/**
 * Right-rail detail panel for an inventory item. Same chrome family as
 * `BookingSidePanel` (Drawer + Tabs + sticky footer) — there's no Pencil
 * frame for an Item Detail so we model after the booking side panel.
 *
 * Tabs:
 *   * **Overview**      — read-only fields with an "Edit" toggle that flips
 *                          the rows to inputs and PATCHes on save.
 *   * **Stock movements**— per-item ledger (`?itemId=`) + Stock In CTA on
 *                          physical items.
 *   * **Linked services**— placeholder until a backend item→services index
 *                          ships; CTA pointing the user to /services.
 */
type TabKey = "overview" | "movements" | "links";

export function InventoryItemPanel({
  itemId,
  onClose,
}: {
  itemId: string | null;
  onClose: () => void;
}) {
  const open = !!itemId;
  const [tab, setTab] = useState<TabKey>("overview");
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Per-item detail fetch — embeds `customFieldValues` alongside the row
  // so the panel can render the schema-driven Custom fields tab without a
  // second roundtrip. The list page keeps using the slimmer overview query.
  const detailQ = useInventoryItemDetail(itemId);
  const item = detailQ.data ?? null;

  // Reset to Overview every time a new item opens so the operator never
  // re-opens into a tab that doesn't apply to the new item's kind.
  useEffect(() => {
    if (itemId) setTab("overview");
  }, [itemId]);

  const patch = usePatchInventoryItem(itemId ?? "");
  const toast = useToast();
  const qc = useQueryClient();

  const onSoftDelete = () => {
    if (!itemId) return;
    patch.mutate(
      { active: false },
      {
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: ["inventory"] });
          setConfirmDelete(false);
          onClose();
          toast.success("Item hidden", "Existing bookings keep their snapshots.");
        },
        onError: (e) => {
          toast.error("Couldn't delete", (e as Error).message);
          setConfirmDelete(false);
        },
      },
    );
  };

  return (
    <>
      <Drawer open={open} onClose={onClose} width="lg">
        {!item && (
          <DrawerBody className="px-5 py-6">
            <p className="text-sm text-muted-foreground">Loading…</p>
          </DrawerBody>
        )}

        {item && (
          <>
            <DrawerHeader
              title={
                <div className="flex items-center gap-2">
                  {item.kind === "plan" ? (
                    <Sparkles className="h-4 w-4 text-primary" />
                  ) : (
                    <Boxes className="h-4 w-4 text-foreground/70" />
                  )}
                  <span>{item.name}</span>
                </div>
              }
              subtitle={item.category ?? "Uncategorized"}
              status={
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    item.kind === "plan"
                      ? "bg-primary/10 text-primary"
                      : "bg-muted text-foreground"
                  }`}
                >
                  {KIND_LABELS[item.kind as InventoryKind] ?? item.kind}
                </span>
              }
              onClose={onClose}
            />

            <div className="px-5 py-4 grid grid-cols-3 gap-4 border-b border-border">
              <Stat
                label="Price"
                value={
                  item.unitPrice != null
                    ? formatMoney(item.currency, item.unitPrice)
                    : "—"
                }
              />
              <Stat
                label="Stock"
                value={
                  hasStock(item.kind) ? (
                    `${item.qtyOnHand}${item.unit ? ` ${item.unit}` : ""}`
                  ) : (
                    <span className="text-muted-foreground italic">Unlimited</span>
                  )
                }
              />
              <Stat
                label="Status"
                value={<ItemStatusBadge item={item} />}
              />
            </div>

            <Tabs<TabKey>
              value={tab}
              onChange={setTab}
              className="mt-3 px-5"
              items={[
                { key: "overview", label: "Overview" },
                { key: "movements", label: "Stock movements" },
                { key: "links", label: "Linked services" },
              ]}
            />

            <DrawerBody className="px-5 py-5">
              {tab === "overview" && <OverviewTab item={item} />}
              {tab === "movements" && <MovementsTab item={item} />}
              {tab === "links" && <LinksTab />}
            </DrawerBody>

            <DrawerFooter className="justify-between">
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:bg-status-bg-cancelled"
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={onClose}>
                  Close
                </Button>
              </div>
            </DrawerFooter>
          </>
        )}
      </Drawer>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={onSoftDelete}
        title={`Delete "${item?.name ?? ""}"?`}
        description="This is a soft-delete — the item is hidden from inventory and from new booking forms. Existing bookings keep their snapshot of name + price."
        confirmLabel="Yes, delete"
        pending={patch.isPending}
      />
    </>
  );
}

function ItemStatusBadge({ item }: { item: InventoryItem }) {
  // Non-stock kinds (PLAN, ADDON) just show active/draft.
  if (!hasStock(item.kind)) return <StatusBadge label={item.active ? "active" : "draft"} />;
  if (!item.active) return <StatusBadge label="cancelled" />;
  if (item.qtyOnHand === 0) return <StatusBadge label="cancelled" />;
  return <StatusBadge label="active" />;
}

function OverviewTab({ item }: { item: InventoryItemDetail }) {
  const [editing, setEditing] = useState(false);
  const patch = usePatchInventoryItem(item.id);
  const toast = useToast();
  const fields = item.customFieldValues ?? [];
  const allCategories = useInventoryCategories();

  // Local form state mirrors the entity. Cancel rolls everything back.
  const [name, setName] = useState(item.name);
  const [category, setCategory] = useState(item.category ?? "");
  const [description, setDescription] = useState(item.description ?? "");
  const [unitPrice, setUnitPrice] = useState(String(item.unitPrice ?? ""));
  const [currency, setCurrency] = useState(item.currency ?? "PHP");
  const [customDraft, setCustomDraft] = useState<Record<string, string>>(
    () => initialDraft(fields),
  );

  // Re-seed when a different item opens or its embedded values shift.
  useEffect(() => {
    setCustomDraft(initialDraft(fields));
  }, [item.id, fields]);

  const start = () => {
    setName(item.name);
    setCategory(item.category ?? "");
    setDescription(item.description ?? "");
    setUnitPrice(String(item.unitPrice ?? ""));
    setCurrency(item.currency ?? "PHP");
    setCustomDraft(initialDraft(fields));
    setEditing(true);
  };

  const save = () => {
    const customFieldValues = fields.map((f) => ({
      fieldId: f.fieldId,
      value: customDraft[f.fieldId]?.trim() || null,
    }));
    patch.mutate(
      {
        name: name.trim(),
        category: category.trim() || undefined,
        description: description.trim() || undefined,
        unitPrice: unitPrice.trim() ? Number(unitPrice) : undefined,
        currency: currency.trim() || undefined,
        customFieldValues: fields.length ? customFieldValues : undefined,
      },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success("Saved");
        },
        onError: (e) => toast.error("Couldn't save", (e as Error).message),
      },
    );
  };

  if (editing) {
    return (
      <section className="rounded-lg border bg-card">
        <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
            Edit item
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={patch.isPending}>
              {patch.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
        <div className="px-4 py-4 space-y-3">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Category">
            <Select value={category || undefined} onValueChange={setCategory}>
              <SelectTrigger className="h-10">
                <SelectValue placeholder="Pick a category…" />
              </SelectTrigger>
              <SelectContent>
                {(allCategories.data ?? []).map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Description">
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Price">
              <Input
                type="number"
                step="0.01"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
              />
            </Field>
            <Field label="Currency">
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.iso} value={c.iso}>
                      <span className="inline-flex items-center gap-2">
                        <span className="font-semibold w-5 text-center">{c.symbol}</span>
                        <span>{c.name}</span>
                        <span className="text-muted-foreground text-xs">({c.iso})</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          {fields.length > 0 && (
            <div className="border-t border-border pt-3 space-y-3">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                Custom fields
              </p>
              {fields.map((f) => (
                <Field
                  key={f.fieldId}
                  label={`${f.label}${f.required ? " *" : ""}`}
                >
                  <CustomFieldInput
                    field={{ type: f.type, options: f.options, required: f.required }}
                    value={customDraft[f.fieldId] ?? ""}
                    onChange={(v) =>
                      setCustomDraft((d) => ({ ...d, [f.fieldId]: v }))
                    }
                  />
                </Field>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-lg border bg-card">
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
          Item details
        </p>
        <button
          type="button"
          onClick={start}
          className="text-xs text-primary hover:underline inline-flex items-center gap-1"
        >
          <Pencil className="h-3 w-3" /> Edit
        </button>
      </div>
      <div className="px-4 py-3 space-y-1">
        <FieldPair label="Name" value={item.name} />
        <FieldPair label="Category" value={item.category ?? "—"} />
        {item.sku && <FieldPair label="SKU" value={item.sku} />}
        <FieldPair label="Kind" value={KIND_LABELS[item.kind as InventoryKind] ?? item.kind} />
        {item.unit && <FieldPair label="Unit" value={item.unit} />}
        <FieldPair
          label="Price"
          value={
            item.unitPrice != null
              ? formatMoney(item.currency, item.unitPrice)
              : "—"
          }
        />
        {item.kind === "physical" && (
          <>
            {item.costPerUnit != null && (
              <FieldPair
                label="Cost per unit"
                value={formatMoney(item.currency, item.costPerUnit)}
              />
            )}
          </>
        )}
        {item.description && (
          <FieldPair label="Description" value={item.description} />
        )}
        {fields.map((f) => (
          <FieldPair
            key={f.fieldId}
            label={f.label}
            value={
              f.value ? (
                f.type === "checkbox" ? (
                  f.value === "true" ? "Yes" : "No"
                ) : (
                  f.value
                )
              ) : (
                <span className="text-muted-foreground italic">—</span>
              )
            }
          />
        ))}
      </div>
    </section>
  );
}

function MovementsTab({ item }: { item: InventoryItem }) {
  const movements = useInventoryMovements(item.id);
  const stock = useStockMovement(item.id);
  const toast = useToast();
  const [showStockIn, setShowStockIn] = useState(false);
  const [qty, setQty] = useState("1");
  const [note, setNote] = useState("");

  const submit = () => {
    const n = Number(qty);
    if (!Number.isFinite(n) || n <= 0) {
      toast.warning("Pick a positive quantity");
      return;
    }
    stock.mutate(
      { itemId: item.id, movementType: "stock_in", qty: n, note: note.trim() || undefined },
      {
        onSuccess: () => {
          setShowStockIn(false);
          setQty("1");
          setNote("");
          toast.success("Stocked in", `+${n} ${item.unit ?? ""}`);
        },
        onError: (e) => toast.error("Couldn't record", (e as Error).message),
      },
    );
  };

  if (!hasStock(item.kind)) {
    return (
      <EmptyState
        bare
        icon={HistoryIcon}
        title={
          item.kind === "plan"
            ? "Plans don't track stock"
            : "Add-ons don't track stock"
        }
        description={
          item.kind === "plan"
            ? "Plan tiers represent abstract pricing options — they're always available regardless of how many bookings select them."
            : "Add-ons (service fees, upgrades) are conceptually unlimited — no quantity, no movements to record."
        }
      />
    );
  }

  const list = movements.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Movements ({list.length})</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Every stock-in / stock-out / adjustment recorded for this item.
          </p>
        </div>
        {!showStockIn && (
          <Button size="sm" onClick={() => setShowStockIn(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Stock In
          </Button>
        )}
      </div>

      {showStockIn && (
        <div className="rounded-lg border bg-primary-light/40 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Quantity">
              <Input
                autoFocus
                type="number"
                min={1}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
              />
            </Field>
            <Field label="Note (optional)">
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Restock from supplier"
              />
            </Field>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowStockIn(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={submit} disabled={stock.isPending}>
              {stock.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}

      {list.length === 0 ? (
        <EmptyState
          bare
          icon={HistoryIcon}
          title="No movements yet"
          description="Stock-in or out events will show up here as you receive or use stock."
        />
      ) : (
        <ol className="rounded-lg border bg-card divide-y">
          {list.map((m) => {
            const positive = m.qtyDelta > 0;
            const Icon = positive ? ArrowUp : ArrowDown;
            return (
              <li key={m.id} className="px-4 py-3 flex items-center gap-3 text-sm">
                <span
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${
                    positive
                      ? "bg-status-bg-completed text-status-completed"
                      : "bg-status-bg-cancelled text-status-cancelled"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">
                    {humanMovement(m.movementType)} ·{" "}
                    <span
                      className={`tabular-nums ${
                        positive ? "text-status-completed" : "text-status-cancelled"
                      }`}
                    >
                      {positive ? "+" : ""}
                      {m.qtyDelta}
                    </span>
                  </p>
                  {m.note && (
                    <p className="text-xs text-muted-foreground mt-0.5">{m.note}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {new Date(m.createdAt).toLocaleString()}
                    {m.bookingId && (
                      <>
                        {" · "}
                        <Link
                          to={`/bookings?focus=${m.bookingId}`}
                          className="text-primary hover:underline"
                        >
                          View booking
                        </Link>
                      </>
                    )}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function LinksTab() {
  return (
    <EmptyState
      bare
      icon={Link2}
      title="Linked services view is wiring up"
      description="A reverse index from inventory items to services lands when the backend exposes /api/v1/inventory/items/{id}/services. For now, manage links from each service's Inventory tab."
      action={
        <Button asChild size="sm" variant="outline">
          <Link to="/services">
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open Services
          </Link>
        </Button>
      }
    />
  );
}

/** Seed the edit-mode draft from the embedded value snapshots. */
function initialDraft(fields: InventoryCustomFieldValue[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of fields) out[f.fieldId] = f.value ?? "";
  return out;
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </p>
      <div className="mt-1 text-sm font-semibold">{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function humanMovement(t: string): string {
  switch (t) {
    case "stock_in":
      return "Stock in";
    case "stock_out":
      return "Stock out";
    case "adjust":
      return "Adjustment";
    case "usage":
      return "Used in booking";
    default:
      return t;
  }
}

// Silence unused — `CircleCheck` / `AlertTriangle` reserved for the future
// Linked services tab once the backend index lands.
void CircleCheck;
void AlertTriangle;
